import { SearchSortFilterReducerState } from '@components/reviews/components/search-sort-filter/types';
import { currentResultsCountAtom } from '@components/reviews/shared-atoms';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { useBaseStorefrontAnalytics } from '@hooks/useBaseStorefrontAnalytics';
import { useAtomValue, useSetAtom } from 'jotai';
import { useReducerAtom } from 'jotai/utils';
import { useEffect, useMemo } from 'react';
import {
	fetchReviewsAtom,
	reviewsLoadableStatesAtom,
	searchSortFilterAtom,
	searchSortFilterReducer,
	searchTextDisplayAtom,
} from '../atoms';

const SORT_TYPE_MAP = {
	'selectiontype:asc|createddate:desc': 'Top Reviews',
	'createddate:desc': 'Newest First',
	'createddate:asc': 'Oldest First',
	'rating:desc': 'Highest Rated',
	'rating:asc': 'Lowest Rated',
} as const;

const ACTION_MAP = {
	search: 'initiate search',
	'star-rating': 'filter reviews',
	sort: 'sort reviews',
} as const;

const STAR_TEXT_MAP = {
	1: 'One star',
	2: 'Two stars',
	3: 'Three stars',
	4: 'Four stars',
	5: 'Five stars',
} as const;

const ACTIONS_TO_TRACK_AFTER_FETCH: Partial<
	SearchSortFilterReducerState['lastAction']
>[] = ['search', 'star-rating', 'sort'];

export const useSearchSortFilterReviews = () => {
	const [state, dispatch] = useReducerAtom(
		searchSortFilterAtom,
		searchSortFilterReducer,
	);
	const setSearchTextDisplay = useSetAtom(searchTextDisplayAtom);
	const loadableReview = useAtomValue(reviewsLoadableStatesAtom);

	const baseStorefrontAnalytics = useBaseStorefrontAnalytics();
	const { track } = useAnalyticsContext();
	const fetchReviews = useSetAtom(fetchReviewsAtom);
	const resultsCount = useAtomValue(currentResultsCountAtom);

	const trackSearchSortFilterAfterFetch = (numberOfResults: number) => {
		const {
			lastAction,
			queryParams: { content, sort, stars },
		} = state;
		if (!lastAction || !ACTIONS_TO_TRACK_AFTER_FETCH.includes(lastAction))
			return;
		const analyticsProperties = {
			...baseStorefrontAnalytics,
			numberOfResults,
			sortType: SORT_TYPE_MAP[sort],
			...(content && { searchString: content }),
			...(stars &&
				stars.length > 0 && {
					filterType: 'Rating',
					selection: stars
						.map((star) => STAR_TEXT_MAP[star as keyof typeof STAR_TEXT_MAP])
						.join(', '),
				}),
			action: ACTION_MAP[lastAction as keyof typeof ACTION_MAP],
		};
		track({
			event: 'Review Interaction',
			properties: analyticsProperties,
		});
	};

	const displayText = useMemo(() => {
		if (
			loadableReview.state === 'hasError' &&
			(loadableReview.error as Error)?.name !== 'AbortError'
		) {
			return 'Something went wrong. Please try again.';
		}
		return '';
	}, [loadableReview.state]);

	useEffect(() => {
		if (loadableReview.state === 'hasData') {
			trackSearchSortFilterAfterFetch(resultsCount);
		}
	}, [loadableReview.state]);

	const setSortSelection = (sort: string) => {
		dispatch({ type: 'sort', payload: sort });
		fetchReviews();
	};

	const setSearchText = (content: string | undefined) => {
		dispatch({ type: 'search', payload: content });
		fetchReviews();
	};

	const clearSearchText = () => {
		setSearchTextDisplay('');
		dispatch({ type: 'search', payload: undefined });
		fetchReviews();
	};

	const clearAll = () => {
		setSearchTextDisplay('');
		dispatch({ type: 'reset' });
		fetchReviews();
	};

	return {
		setSortSelection,
		clearSearchText,
		setSearchText,
		clearAll,
		displayText,
	};
};
